import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Upload, Download, X, Image as ImageIcon, Video, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const EventPhotosPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photoData, setPhotoData] = useState(null);

  useEffect(() => {
    fetchEventPhotos();
  }, [eventId]);

  const fetchEventPhotos = async () => {
    setLoading(true);
    try {
      const [eventRes, photosRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/${eventId}/photos`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setEvent(eventRes.data);
      setPhotoData(photosRes.data);
      setPhotos(photosRes.data.photos || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API}/events/${eventId}/photos/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`Uploaded ${response.data.uploaded} file(s)!`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(error => toast.error(error));
      }

      setSelectedFiles([]);
      fetchEventPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSingle = async (photoId, fileName) => {
    try {
      const response = await axios.get(
        `${API}/events/${eventId}/photos/${photoId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  const handleDownloadAll = async () => {
    try {
      toast.info('Preparing ZIP file...');
      const response = await axios.get(
        `${API}/events/${eventId}/photos/download-all`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${event.title}_photos.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('All photos downloaded!');
    } catch (error) {
      toast.error('Failed to download photos');
    }
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const formatTimeRemaining = () => {
    if (!photoData?.deletion_date) return '';
    
    const now = new Date();
    const deletion = new Date(photoData.deletion_date);
    const diff = deletion - now;
    
    if (diff < 0) return 'Photos will be deleted soon';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-contribo-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(`/event/${eventId}`)} className="mb-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Event
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{event?.title} - Photos</h1>
          <p className="text-gray-600 mt-2">Share and view event memories</p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Banner */}
        {photoData && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{formatTimeRemaining()}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Photos: {photoData.total_count} / 50 | Size: {photoData.total_size_mb} / 100 MB
                  </p>
                  {photoData.deletion_date && (
                    <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Auto-delete: {new Date(photoData.deletion_date).toLocaleString()}
                    </p>
                  )}
                </div>
                {photos.length > 0 && (
                  <Button onClick={handleDownloadAll} className="bg-green-600 hover:bg-green-700">
                    <Download className="mr-2 h-4 w-4" />
                    Download All
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Section (Hosts/Co-hosts only) */}
        {photoData?.can_upload && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Photos/Videos
              </h2>
              
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,video/*"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-contribo-navy file:text-white hover:file:bg-contribo-navy/90"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Max: 50 photos OR 100 MB | Photo: 10 MB | Video: 25 MB | Types: JPG, PNG, Videos
                  </p>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="bg-gray-100 px-3 py-1 rounded-full text-xs flex items-center gap-2">
                        {file.type.startsWith('video/') ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                        {file.name}
                        <button onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== idx))}>
                          <X className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <Button 
                  onClick={handleUpload} 
                  disabled={uploading || selectedFiles.length === 0}
                  className="w-full"
                >
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos Grid */}
        {photos.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No photos uploaded yet</p>
              {photoData?.can_upload && (
                <p className="text-sm text-gray-400 mt-2">Be the first to share memories!</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {photos.map((photo, index) => (
              <div key={photo.id} className="relative group cursor-pointer" onClick={() => openLightbox(index)}>
                <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                  {photo.file_type === 'image' ? (
                    <img
                      src={`${API}/events/${eventId}/photos/${photo.id}/download`}
                      alt={photo.file_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <Video className="h-12 w-12 text-white" />
                    </div>
                  )}
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadSingle(photo.id, photo.file_name);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <button className="absolute top-4 right-4 text-white" onClick={closeLightbox}>
            <X className="h-8 w-8" />
          </button>
          
          <button className="absolute left-4 text-white" onClick={(e) => { e.stopPropagation(); prevPhoto(); }}>
            <ArrowLeft className="h-8 w-8" />
          </button>
          
          <div className="max-w-4xl max-h-screen p-4" onClick={(e) => e.stopPropagation()}>
            {photos[currentPhotoIndex].file_type === 'image' ? (
              <img
                src={`${API}/events/${eventId}/photos/${photos[currentPhotoIndex].id}/download`}
                alt={photos[currentPhotoIndex].file_name}
                className="max-w-full max-h-[80vh] object-contain mx-auto"
              />
            ) : (
              <video
                controls
                className="max-w-full max-h-[80vh] mx-auto"
                src={`${API}/events/${eventId}/photos/${photos[currentPhotoIndex].id}/download`}
              />
            )}
            <p className="text-white text-center mt-4">{photos[currentPhotoIndex].file_name}</p>
            <p className="text-gray-400 text-center text-sm">
              {currentPhotoIndex + 1} / {photos.length}
            </p>
          </div>
          
          <button className="absolute right-4 text-white" onClick={(e) => { e.stopPropagation(); nextPhoto(); }}>
            <ArrowLeft className="h-8 w-8 rotate-180" />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EventPhotosPage;
